/**
 * The one thing standing between a render-time exception and a blank page.
 *
 * React unmounts the **whole tree** when a render throws and nothing catches it: no error,
 * no toast, no map, no way back except a reload the reader has to think of. There was no
 * boundary anywhere in `src/` until an §E7 oversized result reached a §H3 panel and every
 * `result.rows.length` in the dock became a TypeError (NOTES #650). That particular hole is
 * closed at its source in `lib/analysis.ts`; this is the answer to the class of it.
 *
 * **Two boundaries, not one**, because "reload the portal" is the wrong offer for a panel
 * that failed:
 *
 * * `App` wraps the routed view, so a failure anywhere below it leaves the reader a message
 *   and a reload button instead of white;
 * * the §F6 dock wraps each tool panel, so a bad result loses the panel and keeps the map,
 *   the legend and the rest of the dock.
 *
 * A class component, because `componentDidCatch`/`getDerivedStateFromError` have no hook
 * equivalent — this is the one place in the app that has to be one.
 *
 * The copy goes through `t()` like everything else, with an English literal as the last
 * resort: the boundary has to render when the app is already broken, and i18n's own
 * initialisation is part of the app.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { withTranslation, type WithTranslation } from 'react-i18next';

interface Props extends WithTranslation {
  children: ReactNode;
  /** `page` offers a reload; `panel` is inline and offers nothing. */
  variant?: 'page' | 'panel';
  /** Test seam and telemetry hook — called once per caught error. */
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface State {
  error: Error | null;
}

class Boundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // The console is the only place this can go: there is no error-reporting endpoint in
    // §D and inventing one here would be a new network call on the worst code path there is.
    console.error('ErrorBoundary caught a render error', error, info.componentStack);
    this.props.onError?.(error, info);
  }

  override render() {
    const { error } = this.state;
    const { children, variant = 'page', t } = this.props;
    if (!error) return children;

    if (variant === 'panel') {
      return (
        <div className="note" data-role="boundary" role="alert">
          {t('error.panelCrashed', 'Something went wrong showing this result.')}
        </div>
      );
    }
    return (
      <div className="boundary" data-role="boundary" role="alert">
        <h2>{t('error.crashedTitle', 'Something went wrong')}</h2>
        <p>{t('error.crashed', 'The page could not be displayed. Reloading usually fixes it.')}</p>
        <button type="button" className="btn" onClick={() => window.location.reload()}>
          {t('error.reload', 'Reload')}
        </button>
      </div>
    );
  }
}

export const ErrorBoundary = withTranslation()(Boundary);

export default ErrorBoundary;
